import { ReactNode, useMemo } from 'react';
import { Dictionary } from '@reduxjs/toolkit';

import createSafeContext from '#/utils/createSafeContext';
import {
  MarketsState,
  useOptionMarketsState,
} from '#/features/options/markets-context';
import { Market } from '#/api/markets';
import * as OptionFn from '#/features/options/functions';

interface TableSettings {
  columns: ColumnSettings[];
}

export interface ColumnSettings {
  key: keyof MarketValuesView;
  header: ReactNode;
  visible: boolean;
}

interface TablesView {
  readonly status: 'loading' | 'error' | 'done';
  readonly error: string;
  readonly tables: Dictionary<TableView>;
}

export interface TableView {
  expiryDate: string;
  rows: TableRowView[];
}

interface TableRowView {
  CALL: MarketValuesView;
  strikePriceQuoteCurrency: string;
  PUT: MarketValuesView;
}

export interface MarketValuesView {
  symbol: string;
  // bestBid
  bestBidSize: string;
  bestBidPriceQuoteCurrency: string;
  // bestAsk
  bestAskSize: string;
  bestAskPriceQuoteCurrency: string;
}

export const [useOptionTablesView, OptionTablesViewCtx] =
  createSafeContext<TablesView>('OptionTablesView');

export const [useOptionTableSettings, OptionTableSettingsCtx] =
  createSafeContext<TableSettings>('OptionTableSettings');

interface ProviderProps {
  readonly children: ReactNode;
}

export function OptionTablesProvider(props: ProviderProps) {
  const optionsState = useOptionMarketsState();

  // WIP: to be modifiable by the user
  const tableSettings: TableSettings = {
    columns: [
      { key: 'bestBidSize', header: 'Size', visible: true },
      { key: 'bestBidPriceQuoteCurrency', header: 'Bid', visible: true },
      { key: 'bestAskPriceQuoteCurrency', header: 'Ask', visible: true },
      { key: 'bestAskSize', header: 'Size', visible: true },
    ],
  };

  // WIP: only create tables for currently active asset (from the URL), have separate context to track that.
  const view = useMemo(() => createView(optionsState), [optionsState]);

  return (
    <OptionTableSettingsCtx value={tableSettings}>
      <OptionTablesViewCtx value={view}>{props.children}</OptionTablesViewCtx>
    </OptionTableSettingsCtx>
  );
}

/**
 * Derive required view transforming `MarketsState` into structure that will be rendered as the Options Table
 *
 * Transformation logic:
 * 1. Iterate over every Option Market
 * 2. For each option market identify PUT and CALL symbol
 * 3. Retrieve PUT and CALL markets from `MarketsState` and transform into required Table Row format
 * 4. To avoid duplicate rows, track PUT/CALL Option Markets that already were processed
 *   4.1 For example, given market `BTC-USD-221112-P-17700`, retrieve both PUT and CALL markets and transform them into Table Row.
 *       And later when market `BTC-USD-221112-C-17700` encountered – skip it, as this CALL market was already processed.
 */
function createView(optionsState: MarketsState): TablesView {
  const tables: TablesView['tables'] = {};
  const processedOptions = new Set<string>(); // Lookup set to know which markets we already processed
  const optionsDictionary = optionsState.markets.entities;

  Object.values(optionsDictionary).forEach((option) => {
    const { symbol } = option!;
    const [baseCurrency, quoteCurrency, expiryDate, _type, strikePrice] =
      OptionFn.parseSymbol(symbol);
    const optionIdentifier = `${baseCurrency}-${quoteCurrency}-${expiryDate}-${strikePrice}`; // optimization to achieve reliable O(n) calculation
    const callSymbol = `${baseCurrency}-${quoteCurrency}-${expiryDate}-C-${strikePrice}`;
    const putSymbol = `${baseCurrency}-${quoteCurrency}-${expiryDate}-P-${strikePrice}`;

    if (processedOptions.has(optionIdentifier)) {
      // we already processed PUT / CALL data for this option
      return;
    }

    const table: TableView = tables[expiryDate] ?? { expiryDate, rows: [] };
    const row: TableRowView = {
      strikePriceQuoteCurrency: strikePrice,
      CALL: marketValuesView(optionsState, callSymbol),
      PUT: marketValuesView(optionsState, putSymbol),
    };

    table.rows.push(row);
    tables[expiryDate] = table;
    processedOptions.add(optionIdentifier);
  });

  return {
    status: optionsState.status,
    error: optionsState.error,
    tables,
  };
}

function marketValuesView(
  optionsState: MarketsState,
  symbol: Market['symbol'],
): MarketValuesView {
  const optionsDictionary = optionsState.markets.entities;
  const option = optionsDictionary[symbol];

  if (option == null) {
    return {
      symbol,
      // bestBid
      bestBidSize: '-',
      bestBidPriceQuoteCurrency: '-',
      // bestAsk
      bestAskSize: '-',
      bestAskPriceQuoteCurrency: '-',
    };
  }

  // WIP: values to be derived from `OptionsSummaryState` (options-summary-context)
  return {
    symbol,
    // bestBid
    bestBidSize: '4.6',
    bestBidPriceQuoteCurrency: '$18,000',
    // bestAsk
    bestAskSize: '10.6',
    bestAskPriceQuoteCurrency: '$19,500',
  };
}
